import { useRef } from 'react'
import { Trash2, UploadCloud, FileText, Sheet, FileImage, File } from 'lucide-react'
import { QualityBadge } from '../QualityBadge/index'
import type { ProjectFile } from '../types/analysis'
import styles from './FileRow.module.css'

export type { ProjectFile }

export interface FileRowProps {
  file: ProjectFile
  editable?: boolean
  onDelete?: (id: string) => void
  onReupload?: (id: string, file: File) => void
}

function fileIcon(filename: string) {
  const ext = filename.split('.').pop()?.toLowerCase() ?? ''
  if (ext === 'pdf') return <FileText size={18} />
  if (['csv', 'xlsx', 'xls'].includes(ext)) return <Sheet size={18} />
  if (['jpg', 'jpeg', 'png', 'webp', 'tiff'].includes(ext)) return <FileImage size={18} />
  return <File size={18} />
}

function formatDate(s: string) {
  const d = new Date(s)
  if (isNaN(d.getTime())) return '—'
  return d.toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

export function FileRow({ file, editable = false, onDelete, onReupload }: FileRowProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const hasIssue = file.quality_report?.status === 'error' || file.quality_report?.status === 'warning'
  const qualityStatus = file.quality_report?.status ?? 'ok'

  function handleReplaceClick() {
    inputRef.current?.click()
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0]
    if (f && onReupload) {
      onReupload(file.id, f)
      e.target.value = ''
    }
  }

  return (
    <div className={styles.row}>
      <span className={styles.icon}>{fileIcon(file.filename)}</span>
      <span className={styles.name} title={file.filename}>{file.filename}</span>
      <QualityBadge status={qualityStatus as 'ok' | 'warning' | 'error'} />
      <span className={styles.date}>{formatDate(file.created_at)}</span>
      {editable && (
        <div className={styles.actions}>
          {hasIssue && onReupload && (
            <>
              <button className={styles.replaceBtn} onClick={handleReplaceClick} title="Datei ersetzen">
                <UploadCloud size={15} />
                <span>Ersetzen</span>
              </button>
              <input
                ref={inputRef}
                type="file"
                className={styles.hiddenInput}
                onChange={handleFileChange}
              />
            </>
          )}
          {onDelete && (
            <button
              className={styles.deleteBtn}
              onClick={() => onDelete(file.id)}
              aria-label="Datei löschen"
              title="Datei löschen"
            >
              <Trash2 size={15} />
            </button>
          )}
        </div>
      )}
    </div>
  )
}
